/**
 * AST Validator
 * Validates AST against whitelist rules
 */

import { ValidationError } from './errors.js'

// Allowed AST node types
const ALLOWED_NODE_TYPES = new Set([
  // Program
  'Program',

  // Declarations
  'VariableDeclaration',
  'VariableDeclarator',
  'FunctionDeclaration',

  // Expressions
  'Identifier',
  'Literal',
  'ArrayExpression',
  'ObjectExpression',
  'Property',
  'FunctionExpression',
  'ArrowFunctionExpression',
  'UnaryExpression',
  'BinaryExpression',
  'LogicalExpression',
  'ConditionalExpression',
  'CallExpression',
  'NewExpression', // for new Date(), new Error(), etc.
  'MemberExpression',
  'AssignmentExpression',
  'UpdateExpression',
  'SequenceExpression',
  'TemplateLiteral',
  'TemplateElement',
  'TaggedTemplateExpression',
  'SpreadElement',
  'AwaitExpression',

  // Statements
  'BlockStatement',
  'EmptyStatement', // for standalone semicolons like "i++;"
  'ExpressionStatement',
  'IfStatement',
  'SwitchStatement',
  'SwitchCase',
  'ForStatement',
  'ForInStatement',
  'ForOfStatement',
  'WhileStatement',
  'DoWhileStatement',
  'BreakStatement',
  'ContinueStatement',
  'ReturnStatement',
  'ThrowStatement',
  'TryStatement',
  'CatchClause',

  // Patterns
  'AssignmentPattern',
  'ArrayPattern',
  'ObjectPattern',
  'RestElement',
])

// Forbidden identifiers
const FORBIDDEN_IDENTIFIERS = new Set([
  'eval',
  'Function',
  'require',
  'import',
  'module',
  'exports',
  'global',
  'globalThis',
  'window',
  'self',
  'process',
  '__dirname',
  '__filename',
])

// Forbidden property names
const FORBIDDEN_PROPERTIES = new Set([
  '__proto__',
  'constructor',
  'prototype',
  '__defineGetter__',
  '__defineSetter__',
  '__lookupGetter__',
  '__lookupSetter__',
])

/**
 * Validate AST node and all children
 */
export function validate(ast) {
  const errors = []
  walkNode(ast, errors)

  if (errors.length > 0) {
    const firstError = errors[0]
    throw new ValidationError(firstError.message, firstError.location)
  }
}

function walkNode(node, errors) {
  if (!node || typeof node !== 'object') return

  // Check node type
  if (node.type && !ALLOWED_NODE_TYPES.has(node.type)) {
    errors.push({
      message: `Forbidden syntax: ${node.type}`,
      location: node.loc?.start || null,
    })
    return
  }

  // Check specific patterns
  checkForbiddenPatterns(node, errors)

  // Walk children
  for (const key of Object.keys(node)) {
    if (key === 'loc' || key === 'range' || key === 'start' || key === 'end') continue

    const child = node[key]
    if (Array.isArray(child)) {
      for (const item of child) {
        walkNode(item, errors)
      }
    } else if (child && typeof child === 'object' && child.type) {
      walkNode(child, errors)
    }
  }
}

function checkForbiddenPatterns(node, errors) {
  const loc = node.loc?.start || null

  // Check ThisExpression
  if (node.type === 'ThisExpression') {
    errors.push({ message: "'this' is not allowed", location: loc })
    return
  }

  // Check MetaProperty (import.meta, new.target)
  if (node.type === 'MetaProperty') {
    errors.push({ message: 'MetaProperty is not allowed', location: loc })
    return
  }

  // Check ImportExpression (dynamic import)
  if (node.type === 'ImportExpression') {
    errors.push({ message: 'Dynamic import() is not allowed', location: loc })
    return
  }

  // Check forbidden identifiers
  if (node.type === 'Identifier' && FORBIDDEN_IDENTIFIERS.has(node.name)) {
    errors.push({ message: `'${node.name}' is not allowed`, location: loc })
    return
  }

  // Check forbidden property access
  if (node.type === 'MemberExpression') {
    const propName = node.computed ? null : node.property?.name
    if (propName && FORBIDDEN_PROPERTIES.has(propName)) {
      errors.push({ message: `Access to '${propName}' is forbidden`, location: loc })
      return
    }
  }

  // Check forbidden property in object literal
  if (node.type === 'Property') {
    const keyName = node.key?.name || node.key?.value
    if (keyName && FORBIDDEN_PROPERTIES.has(keyName)) {
      errors.push({ message: `Property '${keyName}' is forbidden`, location: loc })
      return
    }
  }

  // Check NewExpression for dangerous constructors
  if (node.type === 'NewExpression') {
    if (node.callee?.type === 'Identifier') {
      const name = node.callee.name
      if (name === 'Function') {
        errors.push({ message: "'new Function()' is not allowed", location: loc })
        return
      }
    }
  }

  // Check CallExpression for dangerous calls
  if (node.type === 'CallExpression') {
    if (node.callee?.type === 'Identifier') {
      const name = node.callee.name
      if (name === 'eval') {
        errors.push({ message: "'eval()' is not allowed", location: loc })
        return
      }
    }
  }
}
